// @ts-nocheck
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { MessageSquare, Mail, Phone, Target, Send, Users, Clock } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import { parseLocalDate } from '@/utils/formatters';

interface Event {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  status: string;
}

interface CustomEventUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  events: Event[];
}

const CustomEventUpdateModal: React.FC<CustomEventUpdateModalProps> = ({
  isOpen,
  onClose,
  events
}) => {
  const [selectedEventId, setSelectedEventId] = useState('');
  const [subject, setSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [smsMessage, setSmsMessage] = useState('');
  const [enableEmail, setEnableEmail] = useState(true);
  const [enableSms, setEnableSms] = useState(false);
  const [targetAudience, setTargetAudience] = useState('all');
  const [ageGroups, setAgeGroups] = useState<string[]>([]);
  const [registrationStatus, setRegistrationStatus] = useState<string[]>(['paid', 'pending']);
  const [urgency, setUrgency] = useState('normal');
  const [isSending, setIsSending] = useState(false);
  const { toast } = useToast();

  const availableAgeGroups = ['10u', '11u', '12u', '13u', '14u', '15u', '16u', '17u', '18u'];
  const selectedEvent = events.find(e => e.id === selectedEventId);

  const handleAgeGroupChange = (ageGroup: string, checked: boolean) => {
    setAgeGroups(prev => 
      checked 
        ? [...prev, ageGroup]
        : prev.filter(ag => ag !== ageGroup)
    );
  };

  const handleRegistrationStatusChange = (status: string, checked: boolean) => {
    setRegistrationStatus(prev => 
      checked 
        ? [...prev, status]
        : prev.filter(s => s !== status)
    );
  };

  const handleSend = async () => {
    if (!selectedEventId || !subject || (!emailMessage && !smsMessage)) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields and select at least one delivery method.",
        variant: "destructive"
      });
      return;
    }

    if (enableSms && smsMessage.length > 160) {
      toast({
        title: "SMS Too Long",
        description: "SMS message must be 160 characters or less.",
        variant: "destructive"
      });
      return;
    }

    setIsSending(true);

    try {
      const eventPayload = {
        type: 'custom_update',
        eventId: selectedEventId,
        subject,
        emailMessage: enableEmail ? emailMessage : null,
        smsMessage: enableSms ? smsMessage : null,
        targetAudience,
        ageGroups: targetAudience === 'age_groups' ? ageGroups : [],
        registrationStatus,
        urgency
      };

      console.log('🔍 Event notification payload being sent:', JSON.stringify(eventPayload, null, 2));
      
      // Call edge function to send custom event update
      const { data, error } = await invokeEdgeFunction('send-event-notification', {
        body: eventPayload
      });

      console.log('📧 Event notification response:', { data, error, hasData: !!data, hasError: !!error });

      // Fallback test using working email type
      if (error && enableEmail) {
        console.log('⚠️ Testing fallback with admin_notification email type...');
        
        const testPayload = {
          email_type: 'admin_notification',
          to_email: 'nancy@zw2.com',
          data: {
            subject: subject,
            message: emailMessage,
            event_name: selectedEvent?.name || 'Test Event'
          }
        };
        
        console.log('🔍 Fallback email payload being sent:', JSON.stringify(testPayload, null, 2));
        
        const { data: fallbackData, error: fallbackError } = await invokeEdgeFunction('send-email', {
          body: testPayload
        });
        
        console.log('📧 Fallback email response:', { 
          data: fallbackData, 
          error: fallbackError, 
          hasData: !!fallbackData, 
          hasError: !!fallbackError 
        });
      }

      if (error) throw error;

      toast({
        title: "Update Sent Successfully",
        description: `Custom event update sent to ${data.recipientCount} recipients.`,
      });

      // Reset form
      setSelectedEventId('');
      setSubject('');
      setEmailMessage('');
      setSmsMessage('');
      setTargetAudience('all');
      setAgeGroups([]);
      setRegistrationStatus(['paid', 'pending']);
      setUrgency('normal');
      onClose();

    } catch (error: any) {
      console.error('Error sending custom update:', error);
      toast({
        title: "Failed to Send Update",
        description: error.message || "An error occurred while sending the update.",
        variant: "destructive"
      });
    } finally {
      setIsSending(false);
    }
  };

  const getCharacterCount = (text: string, type: 'email' | 'sms') => {
    if (type === 'sms') {
      return `${text.length}/160`;
    }
    return `${text.length} characters`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Send Custom Event Update
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Event Selection */}
          <div className="space-y-2">
            <Label htmlFor="event">Select Event *</Label>
            <Select value={selectedEventId} onValueChange={setSelectedEventId}>
              <SelectTrigger>
                <SelectValue placeholder="Choose an event to send updates for" />
              </SelectTrigger>
              <SelectContent>
                {events.map(event => (
                  <SelectItem key={event.id} value={event.id}>
                    {event.name} - {parseLocalDate(event.start_date).toLocaleDateString()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Subject */}
          <div className="space-y-2">
            <Label htmlFor="subject">Subject Line *</Label>
            <Input
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g., Schedule Change: Game moved to 2 PM"
            />
          </div>

          {/* Delivery Channels */}
          <div className="space-y-4">
            <Label>Delivery Channels *</Label>
            
            <div className="space-y-4">
              {/* Email Option */}
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="enable-email"
                  checked={enableEmail}
                  onCheckedChange={(checked) => setEnableEmail(checked === true)}
                />
                <div className="flex-1 space-y-2">
                  <Label htmlFor="enable-email" className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email Message
                  </Label>
                  {enableEmail && (
                    <>
                      <Textarea
                        value={emailMessage}
                        onChange={(e) => setEmailMessage(e.target.value)}
                        placeholder="Enter your detailed email message here..."
                        rows={4}
                        className="w-full"
                      />
                      <p className="text-xs text-gray-500">
                        {getCharacterCount(emailMessage, 'email')}
                      </p>
                    </>
                  )}
                </div>
              </div>

              {/* SMS Option */}
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="enable-sms"
                  checked={enableSms}
                  onCheckedChange={(checked) => setEnableSms(checked === true)}
                />
                <div className="flex-1 space-y-2">
                  <Label htmlFor="enable-sms" className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    SMS Message (Optional)
                  </Label>
                  {enableSms && (
                    <>
                      <Textarea
                        value={smsMessage}
                        onChange={(e) => setSmsMessage(e.target.value)}
                        placeholder="Short SMS message (160 characters max)"
                        rows={2}
                        className="w-full"
                        maxLength={160}
                      />
                      <p className={`text-xs ${smsMessage.length > 160 ? 'text-red-500' : 'text-gray-500'}`}>
                        {getCharacterCount(smsMessage, 'sms')}
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          <Separator />

          {/* Target Audience */}
          <div className="space-y-4">
            <Label className="flex items-center gap-2">
              <Target className="h-4 w-4" />
              Target Audience
            </Label>
            
            <Select value={targetAudience} onValueChange={setTargetAudience}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Event Registrants</SelectItem>
                <SelectItem value="age_groups">Specific Age Groups</SelectItem>
                <SelectItem value="coaches">Coaches Only</SelectItem>
                <SelectItem value="parents">Parents Only</SelectItem>
              </SelectContent>
            </Select>

            {/* Age Groups Selection */}
            {targetAudience === 'age_groups' && (
              <div className="space-y-2">
                <Label className="text-sm">Select Age Groups:</Label>
                <div className="flex flex-wrap gap-2">
                  {availableAgeGroups.map(ageGroup => (
                    <div key={ageGroup} className="flex items-center space-x-2">
                      <Checkbox
                        id={`age-${ageGroup}`}
                        checked={ageGroups.includes(ageGroup)}
                        onCheckedChange={(checked) => handleAgeGroupChange(ageGroup, checked as boolean)}
                      />
                      <Label htmlFor={`age-${ageGroup}`} className="text-sm">
                        {ageGroup}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Registration Status Filter */}
            <div className="space-y-2">
              <Label className="text-sm">Registration Status:</Label>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="status-paid"
                    checked={registrationStatus.includes('paid')}
                    onCheckedChange={(checked) => handleRegistrationStatusChange('paid', checked as boolean)}
                  />
                  <Label htmlFor="status-paid" className="text-sm">Paid</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="status-pending"
                    checked={registrationStatus.includes('pending')}
                    onCheckedChange={(checked) => handleRegistrationStatusChange('pending', checked as boolean)}
                  />
                  <Label htmlFor="status-pending" className="text-sm">Pending Payment</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="status-comped"
                    checked={registrationStatus.includes('comped')}
                    onCheckedChange={(checked) => handleRegistrationStatusChange('comped', checked as boolean)}
                  />
                  <Label htmlFor="status-comped" className="text-sm">Comped</Label>
                </div>
              </div>
            </div>
          </div>

          {/* Urgency Level */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Urgency Level
            </Label>
            <Select value={urgency} onValueChange={setUrgency}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="high">High Priority</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Preview Section */}
          {selectedEvent && (
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium mb-2">Preview:</h4>
              <div className="space-y-2 text-sm">
                <p><strong>Event:</strong> {selectedEvent.name}</p>
                <p><strong>Subject:</strong> {subject || 'No subject'}</p>
                <div className="flex gap-2">
                  {enableEmail && <Badge variant="outline">Email</Badge>}
                  {enableSms && <Badge variant="outline">SMS</Badge>}
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={onClose} disabled={isSending}>
              Cancel
            </Button>
            <Button 
              onClick={handleSend} 
              disabled={isSending || !selectedEventId || !subject || (!enableEmail && !enableSms)}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {isSending ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Update
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CustomEventUpdateModal;